import { useCallback, useRef, useState } from 'react';
import { SlideOverFuncProps, SlideOverType } from './types';

export let slideOverId = 0;

// Ids of the open slide-overs, oldest first, across every provider: each slide-over is its own
// dialog on <body>, so the one opened last is on top whichever provider holds it.
const openOrder: string[] = [];
const forgetOpen = (id: string) => {
  const index = openOrder.indexOf(id);
  if (index > -1) {
    openOrder.splice(index, 1);
  }
};

export const useSliderStates = () => {
  const [slideOvers, setSlideOverState] = useState<SlideOverType>([]);
  // Latest open token per stored id, so a closer from an earlier open is a no-op.
  const openTokens = useRef(new Map<string, symbol>());

  // Drops entries that are still closed once the exit animation has played. An entry that was
  // reopened in the meantime is kept, and its token with it.
  const removeClosed = useCallback((ids: string[] | 'all') => {
    setTimeout(() => {
      setSlideOverState((slideOvers) =>
        slideOvers.filter((item) => {
          const targeted = ids === 'all' || ids.includes(item.id);
          const remove = targeted && !item.isOpen;
          if (remove) {
            openTokens.current.delete(item.id);
            forgetOpen(item.id);
          }
          return !remove;
        }),
      );
    }, 300);
  }, []);

  const deleteSlideOver = useCallback(
    (id: string) => {
      setSlideOverState((slideOvers) => {
        const newArr = [...slideOvers];
        const index = newArr.findIndex((item) => item.id === id);
        if (index > -1) {
          newArr[index].isOpen = false;
        }
        forgetOpen(id);
        return newArr;
      });
      removeClosed([id]);
    },
    [removeClosed],
  );

  const deleteAllSlideOvers = useCallback(() => {
    setSlideOverState((slideOvers) =>
      slideOvers.map((item) => {
        forgetOpen(item.id);
        return { ...item, isOpen: false };
      }),
    );
    removeClosed('all');
  }, [removeClosed]);

  const slideOver = useCallback(
    (params: SlideOverFuncProps) => {
      const generatedId = slideOverId++ + '';
      const id = params.id || generatedId;
      const token = Symbol(id);
      openTokens.current.set(id, token);
      const isOnTop = openOrder[openOrder.length - 1] === id;
      forgetOpen(id);
      openOrder.push(id);
      setSlideOverState((slideOvers) => {
        if (params.id) {
          const index = slideOvers.findIndex((item) => item.id === params.id);
          if (index > -1) {
            // Already the top slide-over: swap its content in place.
            if (isOnTop) {
              const next = [...slideOvers];
              next[index] = {
                ...params,
                id,
                renderKey: slideOvers[index].renderKey,
                isOpen: true,
              };
              return next;
            }
            // Under another slide-over: mount it again on top, or it updates out of sight.
            return [
              ...slideOvers.filter((item) => item.id !== id),
              { ...params, id, renderKey: generatedId, isOpen: true },
            ];
          }
        }
        return [
          ...slideOvers,
          { ...params, id, renderKey: generatedId, isOpen: true },
        ];
      });
      const deleteCurrent = () => {
        if (openTokens.current.get(id) !== token) {
          return;
        }
        openTokens.current.delete(id);
        deleteSlideOver(id);
      };
      return deleteCurrent;
    },
    [deleteSlideOver, setSlideOverState],
  );
  return {
    deleteAllSlideOvers,
    deleteSlideOver,
    slideOver,
    slideOvers,
  };
};
